import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Row, Col, Spin } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import FileLoaderService, { statuses, getFileStatusText, getNeedsChecking } from '../services/file-loader-service';
import Person from './shared/person';
import ErrorsTooltip from './shared/errors-tooltip';
import FileTotalCount from './shared/file-total-count';
import { fileStatusChange, fileTriggered } from '../utils/events';
import { formatDateLong } from '../utils/format';

const File = ( props ) => {
    const { fileId, className, fileTypes = [], auth, onFileLoaded } = props;

    const [ file, setFile ] = useState( null );
    const [ checking, setChecking ] = useState( false );
    const timeoutRef = useRef();
    const statusChangesRef = useRef( [] );

    useEffect( () => {
        if ( !fileId ) {
            return;
        }
        const checkStatus = async ( prevFile ) => {
            try {
                const fileLoader = FileLoaderService( auth.state );
                const nextFile = await fileLoader.getFile( fileId );
                const needsChecking = getNeedsChecking( nextFile );

                if ( !needsChecking ) {
                    setChecking( false );
                    setFile( nextFile );
                    fileStatusChange.fire( fileId );
                    return;
                }

                const errorChange = nextFile.totalErrorCount !== prevFile.totalErrorCount;
                const statusChange = nextFile.status !== prevFile.status;
                const loadedRecordsChange = nextFile.totalFileRecordLoadedCount !== prevFile.totalFileRecordLoadedCount;
                const stagedRecordsChange = nextFile.totalFileRecordStagedCount !== prevFile.totalFileRecordStagedCount;
                if ( statusChange ) {
                    switch ( nextFile.status ) {
                        case statuses.SCHEMA_VALIDATED:
                            if ( !statusChangesRef.current.includes( statuses.SCHEMA_VALIDATED ) ) {
                                statusChangesRef.current.push( statuses.SCHEMA_VALIDATED );
                                fileStatusChange.fire( nextFile.id );
                            }
                            break;
                        case statuses.SHARED_STAGING:
                            if ( !statusChangesRef.current.includes( statuses.SHARED_STAGING ) &&
                                !statusChangesRef.current.includes( statuses.SCHEMA_VALIDATED )
                            ) {
                                statusChangesRef.current.push( statuses.SHARED_STAGING );
                                fileStatusChange.fire( nextFile.id );
                            }
                            break;
                        case statuses.SHARED_STAGED:
                            if ( !statusChangesRef.current.includes( statuses.SHARED_STAGED ) ) {
                                statusChangesRef.current.push( statuses.SHARED_STAGED );
                                fileStatusChange.fire( nextFile.id );
                            }
                            break;
                        case statuses.LOADER_STAGING:
                            if ( !statusChangesRef.current.includes( statuses.LOADER_STAGING ) &&
                                !statusChangesRef.current.includes( statuses.SHARED_STAGED )
                            ) {
                                statusChangesRef.current.push( statuses.LOADER_STAGING );
                                fileStatusChange.fire( nextFile.id );
                            }
                            break;
                        default:
                            break;
                    }
                    setFile( nextFile );

                } else if ( errorChange ) {
                    fileStatusChange.fire( nextFile.id );
                    setFile( nextFile );
                } else if ( stagedRecordsChange ) {
                    fileStatusChange.fire( nextFile.id );
                    setFile( nextFile );
                } else if ( loadedRecordsChange ) {
                    fileStatusChange.fire( nextFile.id );
                    setFile( nextFile );
                }

                timeoutRef.current = setTimeout( () => checkStatus( nextFile ), 3000 );

            } catch ( error ) {
                console.error( 'Error checking file status', error.status || error.message );
            }
        };
        const loadFile = async () => {
            try {
                const fileLoader = FileLoaderService( auth.state );
                const nextFile = await fileLoader.getFile( fileId ) || {};
                if ( typeof onFileLoaded === 'function' ) {
                    onFileLoaded( nextFile );
                }

                setFile( nextFile );
                setChecking( true );
                checkStatus( nextFile );
            } catch ( error ) {
                console.error( 'Error fetching file', error.status );
                setFile( null );
            }
        };
        loadFile();

        const handleFileTriggered = ( id ) => {
            if ( id === fileId ) {
                loadFile();
            }
        };
        fileTriggered.on( handleFileTriggered );

        return () => {
            fileTriggered.off( handleFileTriggered );
            if ( timeoutRef.current ) {
                clearTimeout( timeoutRef.current );
            }
        };

    }, [ fileId, auth?.state?.claims?.company_id ] );

    const fileType = useMemo( () => {
        const type = file?.initialFileType;
        if ( !type ) {
            return '';
        }
        const match = fileTypes.find( t => t.value.toLowerCase() === type.toLowerCase() );
        return match ? match.text : type;
    }, [ file?.initialFileType, fileTypes ] );

    const initialLoading = file === null;

    return (
        <Form
            className={ classNames( 'file-loader-file', className ) }
            layout="vertical"
        >
            <Row justify="space-between">
                <Col span={ 6 }>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Type</b> }>
                        {
                            initialLoading ?
                                <Spin size="small" data-testid="file-spin" />
                                :
                                <span>{ fileType }</span>
                        }
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Status</b> }>
                        {
                            initialLoading ?
                                <Spin size="small" />
                                :
                                <>
                                    {
                                        file.fileErrors && file.fileErrors.length > 0 ? (
                                            <ErrorsTooltip errors={ file.fileErrors }>
                                                <CloseCircleOutlined className="file-loader-hatch-file-icon failed-icon" />
                                            </ErrorsTooltip>
                                        ) : null
                                    }
                                    { getFileStatusText( file ) }
                                    {
                                        checking && (
                                            <Spin size="small" style={ { marginLeft: '8px' } } />
                                        )
                                    }
                                </>
                        }
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Individual Records in Error</b> }>
                        <FileTotalCount file={ file } countKey='totalFileRecordErrorCount' />
                    </Form.Item>
                </Col>
                <Col span={ 6 }>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Load Date</b> }>
                        {
                            initialLoading ?
                                <Spin size="small" />
                                :
                                <span>
                                    {
                                        file.lastProcessedAt ?
                                            formatDateLong( file.lastProcessedAt )
                                            :
                                            <span>&nbsp;</span>
                                    }
                                </span>
                        }
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Total Records</b> }>
                        <FileTotalCount file={ file } countKey='totalFileRecordCount' />
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Total Validated Records</b> }>
                        <FileTotalCount file={ file } countKey='totalFileRecordStagedCount' />
                    </Form.Item>
                </Col>
                <Col span={ 6 }>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Loaded By</b> }>
                        <Person auth={ auth } personId={ file?.createdByPersonId } source={ file?.source } />
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Total Errors</b> }>
                        <FileTotalCount file={ file } countKey='totalErrorCount' />
                    </Form.Item>
                    <Form.Item label={ <b style={ { fontWeight: 500 } }>Total Loaded Records</b> }>
                        <FileTotalCount file={ file } countKey='totalFileRecordLoadedCount' />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default File;